import React, { useContext, useEffect } from "react"
import i18n from "i18n"
import OptionSwitch from "./OptionSwitch"
import { DOWNLOAD_TYPES, INPUTS } from "hooks/budgeting/useDownloadBudgetConfig"
import PropTypes from "prop-types"
import useEntitlementGuard from "hooks/useEntitlementGuard"
import {
  EXPORT_EXCEL_CLIENT_PRICE,
  EXPORT_EXCEL_COST_PRICE,
  EXPORT_EXCEL_DECOMPOSITION,
  EXPORT_EXCEL_MEASUREMENTS,
  EXPORT_EXCEL_COMMENTS,
  GRANTED,
} from "constants/entitlements"
import SubscriptionContext from "context/subscription/subscriptionContext"

const ExcelOptions = ({
  setDownloadBudgetConfig,
  clientPrice,
  setClientPrice,
  costPrice,
  setCostPrice,
  decomposition,
  setDecomposition,
  measure,
  setMeasure,
  comments,
  setComments,
}) => {
  const tipo = DOWNLOAD_TYPES.EXCEL
  const { subscriptionState } = useContext(SubscriptionContext)

  const [checkEntitlement, entitlementDialog] = useEntitlementGuard()

  const handleEntitlementCheck = (permission, state, setState, inputKey) => {
    const hasPermission =
      permission === GRANTED ||
      subscriptionState?.subscription_entitlements?.some(
        ent => ent.feature_name === permission && ent.is_enabled === true,
      )

    const handleBlocked = () => {
      checkEntitlement(permission, i18n.t(`downloadBudget.${inputKey}`))
    }

    useEffect(() => {
      if (!hasPermission && state) {
        setState(false)
        setDownloadBudgetConfig(tipo, INPUTS[inputKey], false)
      }
    }, [hasPermission, state, setState, setDownloadBudgetConfig, tipo])

    return hasPermission ? null : handleBlocked
  }

  const excelData = [
    {
      i18nKey: "downloadBudget.clientPrice",
      state: clientPrice,
      setState: setClientPrice,
      key: `excel_${INPUTS.CLIENT_PRICE}`,
      inputKey: INPUTS.CLIENT_PRICE,
      tipo: tipo,
      setDownloadBudgetConfig: setDownloadBudgetConfig,
      onToggleBlocked: handleEntitlementCheck(
        EXPORT_EXCEL_CLIENT_PRICE,
        clientPrice,
        setClientPrice,
        "clientPrice",
      ),
    },
    {
      i18nKey: "downloadBudget.costPrice",
      state: costPrice,
      setState: setCostPrice,
      key: `excel_${INPUTS.COST_PRICE}`,
      inputKey: INPUTS.COST_PRICE,
      tipo: tipo,
      setDownloadBudgetConfig: setDownloadBudgetConfig,
      onToggleBlocked: handleEntitlementCheck(
        EXPORT_EXCEL_COST_PRICE,
        costPrice,
        setCostPrice,
        "costPrice",
      ),
    },
    {
      i18nKey: "downloadBudget.decomposition",
      state: decomposition,
      setState: setDecomposition,
      key: `excel_${INPUTS.PART_DECOMPOSITION}`,
      inputKey: INPUTS.PART_DECOMPOSITION,
      tipo: tipo,
      setDownloadBudgetConfig: setDownloadBudgetConfig,
      onToggleBlocked: handleEntitlementCheck(
        EXPORT_EXCEL_DECOMPOSITION,
        decomposition,
        setDecomposition,
        "decomposition",
      ),
    },
    {
      i18nKey: "downloadBudget.measurements",
      state: measure,
      setState: setMeasure,
      key: `excel_${INPUTS.MEASURES}`,
      inputKey: INPUTS.MEASURES,
      tipo: tipo,
      setDownloadBudgetConfig: setDownloadBudgetConfig,
      onToggleBlocked: handleEntitlementCheck(
        EXPORT_EXCEL_MEASUREMENTS,
        measure,
        setMeasure,
        "measurements",
      ),
    },
    {
      i18nKey: "downloadBudget.comments",
      state: comments,
      setState: setComments,
      key: `excel_${INPUTS.COMMENTS}`,
      inputKey: INPUTS.COMMENTS,
      tipo: tipo,
      setDownloadBudgetConfig: setDownloadBudgetConfig,
      onToggleBlocked: handleEntitlementCheck(
        EXPORT_EXCEL_COMMENTS,
        comments,
        setComments,
        "comments",
      ),
    },
  ]

  return (
    <>
      {excelData.map(opt => (
        <OptionSwitch key={opt.key} {...opt} />
      ))}
      {entitlementDialog}
    </>
  )
}

ExcelOptions.propTypes = {
  clientPrice: PropTypes.bool.isRequired,
  comments: PropTypes.bool.isRequired,
  costPrice: PropTypes.bool.isRequired,
  decomposition: PropTypes.bool.isRequired,
  measure: PropTypes.bool.isRequired,

  setClientPrice: PropTypes.func.isRequired,
  setComments: PropTypes.func.isRequired,
  setCostPrice: PropTypes.func.isRequired,
  setDecomposition: PropTypes.func.isRequired,
  setDownloadBudgetConfig: PropTypes.func.isRequired,
  setMeasure: PropTypes.func.isRequired,
}

export default ExcelOptions
